/**
 * Tooth detection — per-tooth outlines from lip/smile crop (CV heuristic).
 *
 * v8 goals vs v7:
 * - Cuts only on strong vertical interdental dark lines (stop mid-tooth fragmentation)
 * - Merge tiny fragments; only split clearly multi-tooth-wide blobs
 * - Contours hug enamel closer (less inward shrink, better gum/tip snap)
 *
 * v7 goals vs v6:
 * - Count driven by image valleys / bright runs (not a fixed tooth count)
 * - Sensitive interdental cuts + recursive wide-segment splits (catch laterals / premolars)
 * - Softer size filters so shorter corner teeth are kept
 *
 * v6 goals vs v5:
 * - Gradient snap for gum / incisal edges (stop overshoot into lip & oral cavity)
 * - No vertical expand — expand was the main source of loose oval outlines
 * - Tighter lateral trim so outlines don't spill into interdental dark gaps
 * - More envelope samples so crowns follow real enamel curvature
 */

import type {
  BoundingBox,
  Point2D,
  ToothDetectOptions,
  ToothDetectionResult,
  ToothDetector,
  ToothOutline,
} from "./types";

let activeDetector: ToothDetector = createHeuristicToothDetector();

export function setToothDetector(detector: ToothDetector): void {
  activeDetector = detector;
}

export function getToothDetector(): ToothDetector {
  return activeDetector;
}

export async function detectTeeth(
  image: HTMLCanvasElement | HTMLImageElement,
  options?: ToothDetectOptions
): Promise<ToothDetectionResult> {
  return activeDetector.detect(image, options);
}

function pointInPolygon(point: Point2D, polygon: Point2D[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi + 1e-9) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function bboxOf(points: Point2D[]): BoundingBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return {
    x: minX,
    y: minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

function polygonArea(points: Point2D[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area) / 2;
}

function smooth1D(values: Float32Array, radius = 2): Float32Array {
  const out = new Float32Array(values.length);
  for (let i = 0; i < values.length; i++) {
    let sum = 0;
    let w = 0;
    for (let k = -radius; k <= radius; k++) {
      const j = i + k;
      if (j < 0 || j >= values.length) continue;
      const weight = radius + 1 - Math.abs(k);
      sum += values[j] * weight;
      w += weight;
    }
    out[i] = w > 0 ? sum / w : values[i];
  }
  return out;
}

function smoothFinite(values: number[], radius = 1): number[] {
  const out = values.slice();
  for (let i = 0; i < values.length; i++) {
    if (!Number.isFinite(values[i])) continue;
    let sum = 0;
    let w = 0;
    for (let k = -radius; k <= radius; k++) {
      const j = i + k;
      if (j < 0 || j >= values.length || !Number.isFinite(values[j])) continue;
      sum += values[j];
      w += 1;
    }
    if (w) out[i] = sum / w;
  }
  return out;
}

function sampleEven(pts: Point2D[], count: number): Point2D[] {
  if (pts.length === 0) return [];
  if (pts.length <= count) return pts.map((p) => ({ ...p }));
  const out: Point2D[] = [];
  const step = (pts.length - 1) / (count - 1);
  for (let i = 0; i < count; i++) {
    out.push({ ...pts[Math.round(i * step)] });
  }
  return out;
}

function envelopeContour(
  top: number[],
  bottom: number[],
  xStart: number,
  pointsPerEdge = 12
): Point2D[] {
  const topPts: Point2D[] = [];
  const botPts: Point2D[] = [];

  for (let i = 0; i < top.length; i++) {
    if (!Number.isFinite(top[i]) || !Number.isFinite(bottom[i])) continue;
    if (bottom[i] - top[i] < 2) continue;
    topPts.push({ x: xStart + i, y: top[i] });
    botPts.push({ x: xStart + i, y: bottom[i] });
  }

  if (topPts.length < 2) return [];

  const topEdge = sampleEven(topPts, pointsPerEdge);
  const botEdge = sampleEven(botPts, pointsPerEdge).reverse();
  const rt = topEdge[topEdge.length - 1];
  const rb = botEdge[0];
  const lb = botEdge[botEdge.length - 1];
  const lt = topEdge[0];

  // Vertical sides only — no outward bulge into neighbor teeth
  return [
    ...topEdge,
    { x: rt.x, y: rt.y + (rb.y - rt.y) * 0.33 },
    { x: rt.x, y: rt.y + (rb.y - rt.y) * 0.66 },
    ...botEdge,
    { x: lt.x, y: lt.y + (lb.y - lt.y) * 0.66 },
    { x: lt.x, y: lt.y + (lb.y - lt.y) * 0.33 },
  ];
}

/**
 * Snap gum (top) and tip (bottom) to the strongest luminance edges in a column.
 * Prevents soft-threshold bleed into gums / oral cavity.
 */
function snapColumnEdges(
  x: number,
  gumY: number,
  tipY: number,
  lum: Float32Array,
  width: number,
  height: number,
  searchPad: number
): { gum: number; tip: number } {
  const yLo = Math.max(0, gumY - searchPad);
  const yHi = Math.min(height - 1, tipY + searchPad);

  let bestGum = gumY;
  let bestGumGrad = -Infinity;
  // Dark → bright when entering enamel from above
  for (let y = yLo + 1; y <= Math.min(tipY, gumY + searchPad); y++) {
    const above = lum[(y - 1) * width + x];
    const here = lum[y * width + x];
    const grad = here - above;
    if (grad > bestGumGrad) {
      bestGumGrad = grad;
      bestGum = y;
    }
  }

  let bestTip = tipY;
  let bestTipGrad = -Infinity;
  // Bright → dark when leaving enamel into lip / cavity
  for (let y = Math.max(gumY, tipY - searchPad); y < yHi; y++) {
    const here = lum[y * width + x];
    const below = lum[(y + 1) * width + x];
    const grad = here - below;
    if (grad > bestTipGrad) {
      bestTipGrad = grad;
      bestTip = y;
    }
  }

  // Only accept snap if gradient is meaningful; otherwise keep seed
  const gum = bestGumGrad > 8 ? bestGum : gumY;
  const tip = bestTipGrad > 8 ? bestTip : tipY;
  if (tip - gum < 3) return { gum: gumY, tip: tipY };
  return { gum, tip };
}

/**
 * Interdental cuts from brightness + vertical gap evidence.
 * Real contacts are dark through most of the crown; mid-tooth shine is not.
 */
function findInterdentalCuts(
  bright: Float32Array,
  gapEvidence: Float32Array,
  xOffset: number,
  minSep: number,
  minDrop = 0.15
): number[] {
  const sm = smooth1D(bright, Math.max(1, Math.round(minSep / 4)));
  const gap = smooth1D(gapEvidence, Math.max(1, Math.round(minSep / 5)));
  const mean = sm.reduce((a, b) => a + b, 0) / Math.max(1, sm.length);
  const candidates: { x: number; score: number }[] = [];

  for (let i = minSep; i < sm.length - minSep; i++) {
    const v = sm[i];
    if (v >= sm[i - 1] || v >= sm[i + 1]) continue;
    if (i >= 2 && v > sm[i - 2]) continue;
    if (i < sm.length - 2 && v > sm[i + 2]) continue;

    const leftPeak = Math.max(
      ...Array.from(sm.slice(Math.max(0, i - minSep), i + 1))
    );
    const rightPeak = Math.max(
      ...Array.from(sm.slice(i, Math.min(sm.length, i + minSep + 1)))
    );
    const peak = Math.min(leftPeak, rightPeak);
    if (peak <= 0) continue;
    const drop = (peak - v) / peak;
    if (drop < minDrop) continue;
    // Reject weak highlight dips on solid enamel
    if (v > mean * 0.92 && gap[i] < 0.18) continue;

    // Prefer columns that are both dark AND have vertical gap evidence
    const score = drop * 1.4 + gap[i] * 1.8 - v / Math.max(1, mean);
    candidates.push({ x: xOffset + i, score });
  }

  candidates.sort((a, b) => b.score - a.score);
  const cuts: number[] = [];
  for (const c of candidates) {
    if (cuts.some((x) => Math.abs(x - c.x) < minSep)) continue;
    cuts.push(c.x);
  }
  return cuts.sort((a, b) => a - b);
}

/** Pad with weaker valleys only when smile geometry clearly expects more teeth. */
function ensureReasonableCuts(
  bright: Float32Array,
  gapEvidence: Float32Array,
  xOffset: number,
  cuts: number[],
  minSep: number,
  targetCount: number
): number[] {
  const needed = Math.max(0, targetCount - 1);
  if (cuts.length >= needed) return cuts;

  const sm = smooth1D(bright, Math.max(1, Math.round(minSep / 4)));
  const gap = smooth1D(gapEvidence, Math.max(1, Math.round(minSep / 5)));
  const candidates: { x: number; score: number }[] = [];
  for (let i = minSep; i < sm.length - minSep; i++) {
    const v = sm[i];
    if (v >= sm[i - 1] || v >= sm[i + 1]) continue;
    const leftPeak = Math.max(
      ...Array.from(sm.slice(Math.max(0, i - minSep), i + 1))
    );
    const rightPeak = Math.max(
      ...Array.from(sm.slice(i, Math.min(sm.length, i + minSep + 1)))
    );
    const peak = Math.min(leftPeak, rightPeak);
    if (peak <= 0) continue;
    const drop = (peak - v) / peak;
    // Still require a real dip — never invent cuts from flat enamel
    if (drop < 0.1) continue;
    candidates.push({
      x: xOffset + i,
      score: drop + gap[i] * 1.2 - v * 0.002,
    });
  }
  candidates.sort((a, b) => b.score - a.score);

  const out = [...cuts];
  for (const c of candidates) {
    if (out.length >= needed) break;
    if (out.some((x) => Math.abs(x - c.x) < minSep * 0.85)) continue;
    out.push(c.x);
  }
  return out.sort((a, b) => a - b);
}

/** Strong mid-gap only — never force a cut through a bright central. */
function strongMidGapCut(
  bright: Float32Array,
  gapEvidence: Float32Array,
  x0: number,
  x1: number,
  xBase: number,
  minSep: number
): number | null {
  const w = x1 - x0 + 1;
  if (w < minSep * 2.2) return null;
  const lo = x0 + Math.floor(w * 0.32);
  const hi = x0 + Math.floor(w * 0.68);
  let bestX = -1;
  let bestScore = -Infinity;
  const sm = smooth1D(bright, 2);
  for (let x = lo; x <= hi; x++) {
    const i = x - xBase;
    if (i < 2 || i >= sm.length - 2) continue;
    const v = sm[i];
    const neigh = (sm[i - 2] + sm[i + 2]) / 2;
    if (neigh <= 0) continue;
    const drop = (neigh - v) / neigh;
    const g = gapEvidence[i] ?? 0;
    if (drop < 0.12 && g < 0.22) continue;
    const score = drop * 1.5 + g * 2;
    if (score > bestScore) {
      bestScore = score;
      bestX = x;
    }
  }
  return bestX >= 0 && bestScore >= 0.28 ? bestX : null;
}

/** Merge fragments that are clearly half of one tooth. */
function mergeNarrowTeeth(
  teeth: ToothOutline[],
  minWidth: number
): ToothOutline[] {
  if (teeth.length < 2) return teeth;
  const sorted = [...teeth].sort((a, b) => a.boundingBox.x - b.boundingBox.x);
  const out: ToothOutline[] = [];

  for (const t of sorted) {
    const prev = out[out.length - 1];
    if (!prev) {
      out.push(t);
      continue;
    }
    const prevW = prev.boundingBox.width;
    const curW = t.boundingBox.width;
    const gap =
      t.boundingBox.x - (prev.boundingBox.x + prev.boundingBox.width);
    const tooNarrow = prevW < minWidth * 0.55 || curW < minWidth * 0.55;
    const close = gap < minWidth * 0.35;
    if (tooNarrow && close) {
      const contour = [...prev.contour, ...t.contour];
      // Keep outer envelope of both as a simple merge of bboxes via combined contour
      const box = bboxOf(contour);
      // Build rectangle-ish contour from combined envelope samples
      const mergedContour = [
        { x: box.x, y: box.y },
        { x: box.x + box.width, y: box.y },
        { x: box.x + box.width, y: box.y + box.height },
        { x: box.x, y: box.y + box.height },
      ];
      // Prefer concatenating resampled edges from both when possible
      const left = prev.boundingBox.x <= t.boundingBox.x ? prev : t;
      const right = left === prev ? t : prev;
      const top: Point2D[] = [];
      const bot: Point2D[] = [];
      for (const src of [left, right]) {
        const pts = src.contour;
        const cy = src.boundingBox.y + src.boundingBox.height * 0.5;
        for (const p of pts) {
          if (p.y <= cy) top.push(p);
          else bot.push(p);
        }
      }
      top.sort((a, b) => a.x - b.x);
      bot.sort((a, b) => b.x - a.x);
      const combined =
        top.length >= 2 && bot.length >= 2
          ? [...sampleEven(top, 8), ...sampleEven(bot, 8)]
          : mergedContour;
      out[out.length - 1] = {
        ...prev,
        contour: combined,
        boundingBox: bboxOf(combined),
        area: polygonArea(combined),
        confidence: Math.max(prev.confidence ?? 0.5, t.confidence ?? 0.5),
      };
    } else {
      out.push(t);
    }
  }
  return out;
}

/**
 * Soft trim: keep columns with tooth likelihood, not only hard binary.
 * Relative peak threshold pulls sides off interdental dark gaps.
 */
function trimSegmentSoft(
  x0: number,
  x1: number,
  colScore: Float32Array,
  xBase: number,
  minW: number
): [number, number] | null {
  let peak = 0;
  for (let x = x0; x <= x1; x++) {
    peak = Math.max(peak, colScore[x - xBase] || 0);
  }
  // Milder trim so outlines keep full crown width
  const thr = Math.max(0.12, peak * 0.22);
  let L = x0;
  let R = x1;
  while (L <= R && (colScore[L - xBase] || 0) < thr) L++;
  while (R >= L && (colScore[R - xBase] || 0) < thr) R--;

  const solid = thr * 1.08;
  while (L < R && (colScore[L - xBase] || 0) < solid) L++;
  while (R > L && (colScore[R - xBase] || 0) < solid) R--;

  if (R - L + 1 < minW * 0.3) return null;
  return [L, R];
}

/** Snap provisional cut toward the darkest column in a local window. */
function snapCutsToGaps(
  cuts: number[],
  profile: Float32Array,
  xBase: number,
  window: number
): number[] {
  return cuts.map((cut) => {
    const i0 = cut - xBase;
    let best = i0;
    let bestV = profile[i0] ?? Infinity;
    for (let d = -window; d <= window; d++) {
      const j = i0 + d;
      if (j < 0 || j >= profile.length) continue;
      if (profile[j] < bestV) {
        bestV = profile[j];
        best = j;
      }
    }
    return xBase + best;
  });
}

export function createHeuristicToothDetector(): ToothDetector {
  return {
    id: "heuristic-per-tooth-v8",
    async detect(image, options = {}) {
      const started = performance.now();
      const canvas =
        image instanceof HTMLCanvasElement
          ? image
          : (() => {
              const c = document.createElement("canvas");
              c.width = image.naturalWidth || image.width;
              c.height = image.naturalHeight || image.height;
              const cctx = c.getContext("2d");
              if (!cctx) throw new Error("2D context unavailable");
              cctx.drawImage(image, 0, 0);
              return c;
            })();

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) throw new Error("2D context unavailable");

      const { width, height } = canvas;
      const { data } = ctx.getImageData(0, 0, width, height);
      const mask = options.mouthMask;
      const maxTeeth = options.maxTeeth ?? 16;

      const lum = new Float32Array(width * height);
      const soft = new Float32Array(width * height);
      const binary = new Uint8Array(width * height);

      // Collect luminance stats inside mask for adaptive threshold
      const samples: number[] = [];
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          if (mask && mask.length >= 3 && !pointInPolygon({ x, y }, mask)) {
            continue;
          }
          const i = (y * width + x) * 4;
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const L = 0.299 * r + 0.587 * g + 0.114 * b;
          lum[y * width + x] = L;
          samples.push(L);
        }
      }

      samples.sort((a, b) => a - b);
      const p40 = samples[Math.floor(samples.length * 0.4)] ?? 130;
      const p55 = samples[Math.floor(samples.length * 0.55)] ?? 145;
      const hardThr = Math.max(122, Math.min(158, p55 - 2));
      const softThr = Math.max(105, Math.min(142, p40 - 4));

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          if (mask && mask.length >= 3 && !pointInPolygon({ x, y }, mask)) {
            continue;
          }
          const idx = y * width + x;
          const i = idx * 4;
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const L = lum[idx];
          const maxc = Math.max(r, g, b);
          const minc = Math.min(r, g, b);
          const sat = maxc === 0 ? 0 : (maxc - minc) / maxc;
          const notGum = r - g < 42 && !(r > g + 16 && r > b + 16 && L < 155);
          const toothish = sat <= 0.38 && notGum && g - b > -30;

          if (toothish && L >= hardThr) {
            binary[idx] = 1;
            soft[idx] = 1;
          } else if (toothish && L >= softThr) {
            soft[idx] = Math.min(1, (L - softThr) / Math.max(1, hardThr - softThr));
          }
        }
      }

      // --- Dense tooth Y-band from soft+hard ---
      const rowDensity = new Float32Array(height);
      for (let y = 0; y < height; y++) {
        let c = 0;
        for (let x = 0; x < width; x++) {
          const idx = y * width + x;
          c += binary[idx] + soft[idx] * 0.6;
        }
        rowDensity[y] = c;
      }
      const rowSm = smooth1D(rowDensity, 2);
      const rowMax = Math.max(...Array.from(rowSm), 1);
      const rowThr = rowMax * 0.16;

      let minY = height;
      let maxY = 0;
      for (let y = 0; y < height; y++) {
        if (rowSm[y] >= rowThr) {
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
      }
      if (maxY <= minY) {
        return emptyResult(started);
      }

      const padY = Math.max(1, Math.round((maxY - minY) * 0.04));
      minY = Math.max(0, minY - padY);
      maxY = Math.min(height - 1, maxY + padY);

      for (let y = 0; y < height; y++) {
        if (y >= minY && y <= maxY) continue;
        for (let x = 0; x < width; x++) {
          const idx = y * width + x;
          binary[idx] = 0;
          soft[idx] = 0;
        }
      }

      let minX = width;
      let maxX = 0;
      for (let y = minY; y <= maxY; y++) {
        for (let x = 0; x < width; x++) {
          if (binary[y * width + x] || soft[y * width + x] > 0.3) {
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
          }
        }
      }
      if (maxX <= minX) return emptyResult(started);

      const bandH = maxY - minY + 1;
      const bandW = maxX - minX + 1;
      // Central-incisor-like width from height; geometry still guides count
      const typicalW = Math.max(10, bandH * 0.4);
      const minToothW = Math.max(6, typicalW * 0.45);
      // Don't allow cuts closer than ~half a typical tooth (stops fragmentation)
      const minSep = Math.max(6, Math.round(typicalW * 0.55));

      const colScore = new Float32Array(bandW);
      const bright = new Float32Array(bandW);
      const gapEvidence = new Float32Array(bandW);
      const yMid0 = minY + Math.floor(bandH * 0.1);
      const yMid1 = minY + Math.floor(bandH * 0.9);
      const gapLumThr = Math.max(95, (samples[Math.floor(samples.length * 0.35)] ?? 120) - 8);

      for (let x = minX; x <= maxX; x++) {
        let hard = 0;
        let softSum = 0;
        let sumL = 0;
        let nL = 0;
        let darkRows = 0;
        let edgeRows = 0;
        let rows = 0;
        for (let y = yMid0; y <= yMid1; y++) {
          const idx = y * width + x;
          hard += binary[idx];
          softSum += soft[idx];
          const L = lum[idx];
          if (binary[idx] || soft[idx] > 0.2) {
            sumL += L;
            nL++;
          }
          rows++;
          // Vertical dark contact line evidence
          if (L < gapLumThr) darkRows++;
          if (x > 0 && x < width - 1) {
            const dL = Math.abs(lum[idx - 1] - lum[idx + 1]);
            if (dL > 18 && L < (lum[idx - 1] + lum[idx + 1]) * 0.5) edgeRows++;
          }
        }
        const li = x - minX;
        const presence = (hard + softSum * 0.5) / (yMid1 - yMid0 + 1);
        const meanColL = nL > 0 ? sumL / nL : 0;
        colScore[li] = presence;
        bright[li] = presence * (0.3 + 0.7 * (meanColL / 255));
        gapEvidence[li] =
          (darkRows / Math.max(1, rows)) * 0.55 +
          (edgeRows / Math.max(1, rows)) * 0.45;
      }

      // Dynamic count from smile width — valleys must still qualify
      const geometryCount = Math.max(
        4,
        Math.min(maxTeeth, Math.round(bandW / typicalW))
      );

      let cuts = findInterdentalCuts(bright, gapEvidence, minX, minSep, 0.14);
      const valleyCount = Math.min(maxTeeth, cuts.length + 1);
      // Prefer observed valleys; only pad toward geometry when under-detected
      const expectedCount = Math.min(
        maxTeeth,
        Math.max(valleyCount, Math.min(geometryCount, valleyCount + 3))
      );

      cuts = ensureReasonableCuts(
        bright,
        gapEvidence,
        minX,
        cuts,
        minSep,
        expectedCount
      );
      cuts = snapCutsToGaps(
        cuts,
        bright,
        minX,
        Math.max(2, Math.round(minToothW * 0.18))
      );

      // Split only blobs that are clearly ≥ ~2 teeth wide AND have a real mid-gap
      const maxSegW = typicalW * 1.85;
      const refinedCuts: number[] = [];
      {
        const nodes = [minX, ...cuts, maxX + 1];
        for (let s = 0; s < nodes.length - 1; s++) {
          const a = Math.floor(nodes[s]);
          const b = Math.floor(nodes[s + 1]) - 1;
          if (s > 0) refinedCuts.push(a);
          const w = b - a + 1;
          if (w > maxSegW) {
            const mid = strongMidGapCut(
              bright,
              gapEvidence,
              a,
              b,
              minX,
              minSep
            );
            if (mid != null) refinedCuts.push(mid);
          }
        }
      }
      cuts = [...new Set(refinedCuts)].sort((a, b) => a - b);
      if (cuts.length + 1 > maxTeeth) {
        const scored = cuts.map((c) => ({
          x: c,
          v: (bright[c - minX] ?? 1) - (gapEvidence[c - minX] ?? 0),
        }));
        scored.sort((a, b) => a.v - b.v);
        cuts = scored
          .slice(0, maxTeeth - 1)
          .map((s) => s.x)
          .sort((a, b) => a - b);
      }

      const bounds = [minX, ...cuts, maxX + 1];
      const teeth: ToothOutline[] = [];

      for (let s = 0; s < bounds.length - 1; s++) {
        let x0 = Math.floor(bounds[s]);
        let x1 = Math.floor(bounds[s + 1]) - 1;
        if (x1 < x0) continue;

        const trimmed = trimSegmentSoft(x0, x1, colScore, minX, minToothW);
        if (!trimmed) continue;
        [x0, x1] = trimmed;

        const tooth = buildAccurateTooth(
          x0,
          x1,
          binary,
          soft,
          lum,
          width,
          height,
          minY,
          maxY,
          bandH
        );
        if (tooth) teeth.push(tooth);
      }

      const merged = mergeNarrowTeeth(teeth, minToothW);

      const ordered = merged
        .sort((a, b) => a.boundingBox.x - b.boundingBox.x)
        .filter((t, i, arr) => {
          const r = t.boundingBox.width / Math.max(1, t.boundingBox.height);
          const edge = i === 0 || i === arr.length - 1;
          const minH = bandH * (edge ? 0.18 : 0.26);
          const minW = minToothW * (edge ? 0.25 : 0.35);
          return (
            t.boundingBox.height >= minH &&
            t.boundingBox.width >= minW &&
            r < 1.65
          );
        })
        .slice(0, maxTeeth)
        .map((t, index) => ({ ...t, id: `tooth-${index}`, index }));

      return {
        teeth: ordered,
        detectorId: "heuristic-per-tooth-v8",
        processingMs: Math.round(performance.now() - started),
        modelLoaded: false,
      };
    },
  };
}

function emptyResult(started: number): ToothDetectionResult {
  return {
    teeth: [],
    detectorId: "heuristic-per-tooth-v8",
    processingMs: Math.round(performance.now() - started),
    modelLoaded: false,
  };
}

function buildAccurateTooth(
  x0: number,
  x1: number,
  binary: Uint8Array,
  soft: Float32Array,
  lum: Float32Array,
  width: number,
  height: number,
  minY: number,
  maxY: number,
  bandH: number
): ToothOutline | null {
  const segW = x1 - x0 + 1;
  if (segW < 3) return null;

  const top = new Array(segW).fill(Number.POSITIVE_INFINITY);
  const bottom = new Array(segW).fill(Number.NEGATIVE_INFINITY);
  let pixels = 0;

  // Slightly above band so gingival margin can be recovered
  const yLo = Math.max(0, minY - Math.max(2, Math.round(bandH * 0.06)));
  const yHi = Math.min(height - 1, maxY + Math.max(2, Math.round(bandH * 0.04)));
  const searchPad = Math.max(4, Math.round(bandH * 0.16));

  for (let x = x0; x <= x1; x++) {
    const li = x - x0;
    let gumY = -1;
    let tipY = -1;

    // Walk top → bottom: gum = first solid tooth run (softer threshold)
    for (let y = yLo; y <= yHi; y++) {
      const idx = y * width + x;
      if (binary[idx] || soft[idx] > 0.28) {
        gumY = y;
        break;
      }
    }
    // Walk bottom ← top: tip = last soft/hard tooth pixel
    for (let y = yHi; y >= yLo; y--) {
      const idx = y * width + x;
      if (binary[idx] || soft[idx] > 0.2) {
        tipY = y;
        break;
      }
    }

    if (gumY < 0 || tipY < 0 || tipY - gumY < 3) continue;

    const snapped = snapColumnEdges(x, gumY, tipY, lum, width, height, searchPad);
    let gy = snapped.gum;
    let ty = snapped.tip;

    // Climb toward gingival margin while enamel stays bright
    for (let step = 0; step < Math.max(2, Math.round(bandH * 0.05)); step++) {
      const y = gy - 1;
      if (y < yLo) break;
      const L = lum[y * width + x];
      const below = lum[gy * width + x];
      if (L < below - 22 || L < softThrApprox(below)) break;
      if (!(binary[y * width + x] || soft[y * width + x] > 0.22)) break;
      gy = y;
    }

    // Tip: never push into dark oral cavity past the gradient edge
    const tipL = lum[ty * width + x];
    for (let step = 0; step < Math.max(1, Math.round(bandH * 0.025)); step++) {
      const y = ty + 1;
      if (y > yHi) break;
      const L = lum[y * width + x];
      if (L < tipL - 22 || L < softThrApprox(tipL)) break;
      if (soft[y * width + x] < 0.12 && !binary[y * width + x]) break;
      ty = y;
    }

    top[li] = gy;
    bottom[li] = ty;
    pixels += ty - gy + 1;
  }

  for (let i = 1; i < segW - 1; i++) {
    if (
      !Number.isFinite(top[i]) &&
      Number.isFinite(top[i - 1]) &&
      Number.isFinite(top[i + 1])
    ) {
      top[i] = (top[i - 1] + top[i + 1]) / 2;
      bottom[i] = (bottom[i - 1] + bottom[i + 1]) / 2;
    }
  }

  // Minimal inward shrink — keep outlines on enamel borders
  for (let i = 0; i < segW; i++) {
    if (!Number.isFinite(top[i])) continue;
    const h = bottom[i] - top[i];
    if (h < 4) continue;
    top[i] = Math.min(bottom[i] - 3, top[i] + 0.35);
    bottom[i] = Math.max(top[i] + 3, bottom[i] - 0.45);
  }

  const topS = smoothFinite(top, 1);
  const botS = smoothFinite(bottom, 1);
  const contour = envelopeContour(topS, botS, x0, 14);
  if (contour.length < 4) return null;

  const box = bboxOf(contour);
  const area = polygonArea(contour);
  if (area < bandH * segW * 0.08) return null;

  return {
    id: "tooth",
    index: 0,
    contour,
    boundingBox: box,
    area,
    confidence: Math.min(0.95, 0.55 + pixels / (bandH * segW + 1) * 0.4),
  };
}

function softThrApprox(refL: number): number {
  return Math.max(95, refL * 0.72);
}
